import { useEffect, useRef, useState } from 'react';

// Facebook's post plugin renders at a fixed 500 px and cannot report its
// height without Facebook's JS SDK, which our CSP (script-src 'self') keeps
// out. So: a fixed-height frame that scrolls, holding an iframe tall enough
// for a long post; on screens narrower than the plugin the iframe is scaled
// down rather than letting the plugin fall back to its cramped 350 px layout.
const PLUGIN_WIDTH = 500;
const IFRAME_HEIGHT = 1400;

/**
 * A public Facebook post shown through Facebook's embedded-post plugin.
 * `embedUrl` is the server-built plugin URL (used verbatim — CSP allows
 * https://www.facebook.com frames); `url` is the post's own permalink for the
 * caption link.
 *
 * Props: { embedUrl, url, title? }
 */
export default function FacebookPostEmbed({ embedUrl, url, title = 'Facebook post' }) {
  const frameRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      setScale(width > 0 && width < PLUGIN_WIDTH ? width / PLUGIN_WIDTH : 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!embedUrl) return null;

  return (
    <figure className="mx-auto w-full max-w-[500px]">
      <div
        ref={frameRef}
        tabIndex={0}
        aria-label={title}
        className="h-[640px] overflow-y-auto overflow-x-hidden rounded-xl bg-white shadow-soft ring-1 ring-cream-300"
      >
        <div style={{ height: IFRAME_HEIGHT * scale }}>
          <iframe
            src={embedUrl}
            title={title}
            width={PLUGIN_WIDTH}
            height={IFRAME_HEIGHT}
            loading="lazy"
            allow="clipboard-write; encrypted-media; picture-in-picture; web-share"
            style={{ border: 0, display: 'block', transform: `scale(${scale})`, transformOrigin: 'top left' }}
          />
        </div>
      </div>
      {url && (
        <figcaption className="mt-2 text-sm">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-maroon-700 hover:underline"
          >
            View the post on Facebook ↗
          </a>
        </figcaption>
      )}
    </figure>
  );
}
